// The Minecraft course island: the pickaxe hovering over a block it is
// chipping away at, throwing voxel debris on every swing.
import React, { useMemo, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import {
  Fitted,
  HaloRing,
  PLATFORM_TOP,
  ShadowBlob,
  longestAxis,
  useCloned,
  usePointerCursor,
} from './helpers';

/* ── Motion ──
   The cycle is three phases: wind up, strike, then ease back to neutral. That
   last one matters — without it the loop ends at the struck pose and snaps
   back to neutral in a single frame. Recovery ends at exactly 0 with zero
   velocity, which is the same place the wind-up begins, so the seam vanishes. */
/** Seconds per full swing. Lower = faster. */
const SWING_PERIOD = 1.5;
/** Shares of the cycle. Recovery takes whatever is left, so they always sum to 1. */
const WINDUP_FRACTION = 0.44;
const STRIKE_FRACTION = 0.12;
const RECOVERY_FRACTION = 1 - WINDUP_FRACTION - STRIKE_FRACTION;
/** Phase at which the head lands — everything else keys off this. */
const IMPACT_AT = WINDUP_FRACTION + STRIKE_FRACTION;
/** How far back it rocks at the top of the wind-up, in radians (negative). */
const SWING_BACK = -0.5;
/** Total travel from the raised pose to the struck pose, in radians. */
const SWING_TRAVEL = 1.2;
/** Angle at the moment of impact. Derived — the clearance maths depends on it,
    so it must never be typed as a literal in two places. */
const STRIKE_ANGLE = SWING_BACK + SWING_TRAVEL;
/** Easing exponents: <1 snaps, >1 eases. */
const WINDUP_EASE = 2;
const STRIKE_EASE = 0.6;
/** Higher settles harder into neutral at the end of the recovery. */
const RECOVERY_EASE = 3;

/**
 * Swing angle for a phase in 0..1. Continuous across the whole cycle, and
 * equal to 0 at both ends so the loop point can't pop.
 */
const swingAngle = (p) => {
  if (p < WINDUP_FRACTION) {
    return SWING_BACK * Math.pow(p / WINDUP_FRACTION, WINDUP_EASE);
  }
  if (p < IMPACT_AT) {
    const s = (p - WINDUP_FRACTION) / STRIKE_FRACTION;
    return SWING_BACK + SWING_TRAVEL * Math.pow(s, STRIKE_EASE);
  }
  const r = (p - IMPACT_AT) / RECOVERY_FRACTION;
  return STRIKE_ANGLE * Math.pow(1 - r, RECOVERY_EASE);
};

/* ── Rotation ── */
/** Where the head points at impact: down at the block, angled toward the
    camera so the tool reads in silhouette rather than edge-on. */
const REST_AIM = new THREE.Vector3(0.34, -1, 0.26).normalize();
/** Axis the swing turns about. (0,0,1) swings in the screen plane; (1,0,0)
    would swing toward and away from the camera. */
const SWING_AXIS = new THREE.Vector3(0, 0, 1);
/** Set true if the tool swings handle-first — see the note in Pickaxe. */
const HEAD_FLIP = true;

/* ── Position ── */
/** Size of the pickaxe and the block it is chipping. */
const PICK_LENGTH = 1.9;
const BLOCK_SIZE = 0.84;
/** Sideways / toward-camera offset of the whole swing. [x, z] */
const PIVOT_OFFSET = [1, 0.34];
/** Gap between the head and the block's top face at impact. Raise the whole
    swing by raising this. */
const STRIKE_CLEARANCE = 0.04;
/** How far the pivot drops between the top of the wind-up and impact. */
const PIVOT_DIP = 0.25;

/** Vertical drop from pivot to head tip at impact — half the tool's length,
    foreshortened by the strike angle. */
const REACH = (PICK_LENGTH / 2) * Math.cos(STRIKE_ANGLE);
/** Pivot height at impact, so the head lands on the face instead of through it. */
const STRIKE_PIVOT = BLOCK_SIZE + REACH + STRIKE_CLEARANCE;

/** Where in the cycle the head strikes — debris spawns from here. */
const strikePhase = (t) => (t % SWING_PERIOD) / SWING_PERIOD;

/** Path to the tool, so anything else swinging it can't drift to a stale name. */
export const PICKAXE_MODEL = '/MinecraftPickaxe.glb';

/**
 * The pickaxe's neutral orientation, measured from the mesh rather than guessed
 * as Euler angles.
 *
 * longestAxis picks the head by density (58.6% of this mesh's vertices sit on
 * the head side), which is a heuristic, not a fact: if it ends up swinging
 * handle-first, flip HEAD_FLIP and nothing else needs to change.
 *
 * Exported because the Minecraft landing page swings the same tool on a
 * different schedule — the aim is a property of the model, the timing is not.
 */
export const usePickaxeRest = (object) =>
  useMemo(() => {
    const axis = longestAxis(object);
    if (HEAD_FLIP) axis.negate();
    return new THREE.Quaternion().setFromUnitVectors(axis, REST_AIM);
  }, [object]);

const Pickaxe = ({ top, reduced }) => {
  const object = useCloned(PICKAXE_MODEL);
  const group = useRef();

  const rest = usePickaxeRest(object);

  const swingQuat = useMemo(() => new THREE.Quaternion(), []);

  useFrame(({ clock }) => {
    const g = group.current;
    if (!g) return;
    if (reduced) {
      g.quaternion.copy(rest);
      return;
    }
    const swing = swingAngle(strikePhase(clock.getElapsedTime()));
    // Pre-multiply so the swing turns the tool in world space, around the
    // aimed rest pose rather than replacing it.
    swingQuat.setFromAxisAngle(SWING_AXIS, swing);
    g.quaternion.copy(rest).premultiply(swingQuat);
    // Highest at the top of the wind-up, lowest exactly at impact.
    g.position.y = top + STRIKE_PIVOT + PIVOT_DIP * (STRIKE_ANGLE - swing);
  });

  return (
    <group ref={group} position={[PIVOT_OFFSET[0], top + STRIKE_PIVOT + PIVOT_DIP, PIVOT_OFFSET[1]]}>
      <Fitted object={object} size={PICK_LENGTH} fit="max" align="center" />
    </group>
  );
};

/** The block taking the beating — squashes on each strike. */
const ORE_BURST_SECONDS = 0.82;

const OreBlock = ({ accent, top, reduced }) => {
  const mesh = useRef();
  const coreMaterial = useRef();
  const seamMaterial = useRef();
  const burst = useRef(0);
  const [hovered, setHovered] = useState(false);
  const [reducedTurn, setReducedTurn] = useState(0);
  usePointerCursor(hovered);

  useFrame(({ clock }, delta) => {
    if (!mesh.current) return;

    if (reduced) {
      mesh.current.position.y = top + BLOCK_SIZE / 2;
      mesh.current.rotation.y = reducedTurn * (Math.PI / 2);
      mesh.current.scale.setScalar(hovered ? 1.06 : 1);
      if (coreMaterial.current) coreMaterial.current.emissiveIntensity = hovered ? 0.28 : 0.06;
      if (seamMaterial.current) seamMaterial.current.opacity = hovered ? 0.72 : 0.5;
      return;
    }

    const p = strikePhase(clock.getElapsedTime());
    // A short recoil right after impact, then settle.
    const RECOIL = 0.12;
    const hit = p > IMPACT_AT && p < IMPACT_AT + RECOIL
      ? Math.sin(((p - IMPACT_AT) / RECOIL) * Math.PI)
      : 0;

    let pulse = 0;
    let spin = 0;
    if (burst.current > 0) {
      const progress = 1 - burst.current / ORE_BURST_SECONDS;
      pulse = Math.sin(progress * Math.PI);
      spin = (1 - Math.pow(1 - progress, 3)) * Math.PI * 2;
      burst.current = Math.max(0, burst.current - delta);
    }

    const hoverScale = hovered ? 0.06 : 0;
    mesh.current.position.y = top + BLOCK_SIZE / 2 + pulse * 0.34;
    mesh.current.rotation.y = spin;
    mesh.current.rotation.z = Math.sin(pulse * Math.PI) * 0.08;
    mesh.current.scale.set(
      1 + hit * 0.12 + hoverScale + pulse * 0.15,
      1 - hit * 0.16 + hoverScale + pulse * 0.08,
      1 + hit * 0.12 + hoverScale + pulse * 0.15
    );
    if (coreMaterial.current) {
      coreMaterial.current.emissiveIntensity = 0.06 + (hovered ? 0.22 : 0) + pulse * 1.8;
    }
    if (seamMaterial.current) {
      seamMaterial.current.opacity = Math.min(1, 0.5 + (hovered ? 0.18 : 0) + pulse * 0.42);
    }
  });

  const activate = (event) => {
    event.stopPropagation();
    if (reduced) {
      setReducedTurn((turn) => (turn + 1) % 4);
    } else if (burst.current <= 0) {
      burst.current = ORE_BURST_SECONDS;
    }
  };

  return (
    <mesh
      ref={mesh}
      position={[0, top + BLOCK_SIZE / 2, 0]}
      onPointerOver={(event) => { event.stopPropagation(); setHovered(true); }}
      onPointerOut={() => setHovered(false)}
      onClick={activate}
    >
      <boxGeometry args={[BLOCK_SIZE, BLOCK_SIZE, BLOCK_SIZE]} />
      <meshStandardMaterial
        ref={coreMaterial}
        color="#5B6B7A"
        emissive={accent}
        emissiveIntensity={0.06}
        roughness={0.85}
        metalness={0.05}
      />
      {/* Ore seam catching the light */}
      <mesh scale={1.008}>
        <boxGeometry args={[BLOCK_SIZE, BLOCK_SIZE, BLOCK_SIZE]} />
        <meshBasicMaterial
          ref={seamMaterial}
          color={accent}
          wireframe
          transparent
          opacity={0.5}
          toneMapped={false}
        />
      </mesh>
    </mesh>
  );
};

/** Voxel chips thrown off at each strike — cubes, because Minecraft. */
const Debris = ({ accent, top, reduced }) => {
  const group = useRef();
  const chips = useMemo(
    () =>
      new Array(10).fill(0).map((_, i) => {
        const angle = (i / 10) * Math.PI * 2 + Math.random();
        return {
          key: i,
          dir: [Math.cos(angle), 0.9 + Math.random() * 0.7, Math.sin(angle)],
          speed: 1.5 + Math.random() * 1.2,
          spin: (Math.random() - 0.5) * 6,
          size: 0.09 + Math.random() * 0.09,
        };
      }),
    []
  );

  useFrame(({ clock }) => {
    if (reduced || !group.current) return;
    const p = strikePhase(clock.getElapsedTime());
    // Debris only exists in the window after impact.
    const life = p > IMPACT_AT ? (p - IMPACT_AT) / RECOVERY_FRACTION : -1;

    group.current.children.forEach((chip, i) => {
      if (life < 0) { chip.visible = false; return; }
      chip.visible = true;
      const c = chips[i];
      const t = life * c.speed;
      chip.position.set(
        c.dir[0] * t * 0.5,
        // Up, then gravity takes it back down.
        c.dir[1] * t * 0.6 - 1.6 * t * t * 0.5,
        c.dir[2] * t * 0.5
      );
      chip.rotation.x = c.spin * t;
      chip.rotation.y = c.spin * t * 0.7;
      // Hold, then drop. A straight ramp faded them out fastest in the first
      // instants — which is exactly when they are still bunched on top of the
      // block and unreadable; by the time they had thrown clear enough to see
      // there was nothing left of them.
      chip.material.opacity = Math.max(0, 1 - Math.pow(life, 2.2));
    });
  });

  return (
    <group ref={group} position={[0, top + BLOCK_SIZE + 0.06, 0]}>
      {chips.map(({ key, size }) => (
        <mesh key={key} scale={size}>
          <boxGeometry args={[1, 1, 1]} />
          <meshStandardMaterial
            color={accent}
            emissive={accent}
            // Enough to carry the chips over the bloom threshold, so they read
            // as struck sparks against the deck rather than dark specks.
            emissiveIntensity={0.85}
            roughness={0.7}
            transparent
            opacity={0}
            toneMapped={false}
          />
        </mesh>
      ))}
    </group>
  );
};

const MinecraftIsland = ({ accent, accentInk, reduced }) => {
  const platform = useCloned('/GodotBotPlatform.glb');
  const [top, setTop] = useState(PLATFORM_TOP);

  return (
    <group>
      <Fitted object={platform} size={5} fit="max" onFit={({ height }) => setTop(height)} />
      <ShadowBlob radius={3.1} opacity={0.55} position={[0, top + 0.012, 0]} />
      <HaloRing accent={accent} accentInk={accentInk} radius={2.5} position={[0, top - 0.4, 0]} />

      <OreBlock accent={accent} top={top} reduced={reduced} />
      <Pickaxe top={top} reduced={reduced} />
      <Debris accent={accent} top={top} reduced={reduced} />

      <pointLight
        color={accent}
        intensity={1.5}
        distance={4.5}
        decay={2}
        position={[0, top + 1.1, 0.8]}
      />
    </group>
  );
};

export default MinecraftIsland;
